/**
 * Syncs share-count bumps from FeedContext into screens that keep local post lists.
 * Skips mutations that already existed when the screen mounted (no replay).
 */
import { useEffect, useRef } from 'react';
import { useFeed } from '../context/FeedContext';

export const useFeedShareCountSync = (
  onShareSuccess: (postId: string) => void,
): void => {
  const { feedShareMutationVersion, lastFeedShareMutation } = useFeed();
  const appliedVersionRef = useRef<number | null>(null);
  const onShareSuccessRef = useRef(onShareSuccess);
  onShareSuccessRef.current = onShareSuccess;

  useEffect(() => {
    if (appliedVersionRef.current === null) {
      appliedVersionRef.current = feedShareMutationVersion;
      return;
    }
    if (appliedVersionRef.current === feedShareMutationVersion) {
      return;
    }
    appliedVersionRef.current = feedShareMutationVersion;
    const postId = lastFeedShareMutation?.postId;
    if (postId) {
      onShareSuccessRef.current(postId);
    }
  }, [feedShareMutationVersion, lastFeedShareMutation]);
};

/** Immutable share-count bump helper for local FeedPost arrays. */
export const incrementPostShareCount = <T extends { id: string; shares?: number }>(
  posts: T[],
  postId: string,
): T[] => {
  const normalizedPostId = String(postId);
  let changed = false;
  const next = posts.map(post => {
    if (String(post.id) !== normalizedPostId) return post;
    changed = true;
    return { ...post, shares: (post.shares || 0) + 1 };
  });
  return changed ? next : posts;
};
